/* Corporate service galleries — shared behavior (loaded once, run-once guard).
   1. Scroll reveals for every corporate-service-gallery section (opt-in: no-JS
      and reduced-motion users simply see everything).
   2. The shared photo lightbox: ONE real <dialog> for the whole page, opened
      with showModal() so it sits in the browser top layer (above the store
      chat widget) and the rest of the page goes inert. Keyboard: Escape,
      arrows, trapped Tab. Touch: horizontal swipe steps photos.
   3. Proposal links under each project carry that gallery's service into the
      proposal form — only when the visitor has not chosen one — and leave a
      history entry so Back returns to the project they left.
   Photo anchors keep their native full-image href — clicks before this file
   loads (or with modifier keys) fall through to the image itself. */
(function(){
  'use strict';
  if (window.__corpGalWired) return;
  window.__corpGalWired = true;

  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  function reduced(){ return reduceMQ.matches; }

  /* ---------- Reveals ---------- */
  function armReveals(root){
    if (root.dataset.corpGalInit) return;
    root.dataset.corpGalInit = '1';
    if (!('IntersectionObserver' in window) || reduced()) return;
    root.classList.add('corp-anim');
    var revs = Array.prototype.slice.call(root.querySelectorAll('.corp-rev'));
    var pending = revs.slice();
    function reveal(el){
      if (el.classList.contains('in')) return;
      el.classList.add('in');
      io.unobserve(el);
      var i = pending.indexOf(el);
      if (i > -1) pending.splice(i, 1);
    }
    /* threshold:0 — a ratio threshold can never be met by a taller-than-viewport
       element; the negative bottom margin supplies the "wait for it" instead.
       An entry that reports non-intersecting but sits ABOVE the viewport was
       jumped past (scroll restore, anchor, scrollbar drag) — reveal it too:
       content the visitor already passed must never sit invisible above them. */
    var io = new IntersectionObserver(function(entries){
      entries.forEach(function(en){
        if (en.isIntersecting || en.boundingClientRect.top < 0) reveal(en.target);
      });
    }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
    revs.forEach(function(el){ io.observe(el); });
    /* Sweep failsafe: IO only reports on rendered frames, and instant scroll
       jumps (history restore, in-page anchors, scrollbar drags, background
       tabs) can skip every frame in which an element intersected — stranding
       it at opacity 0. Any pending reveal at or above the reveal line gets
       opened on the next scroll/resize/visibility signal, IO or not. */
    var sweepQueued = false;
    function sweep(){
      sweepQueued = false;
      if (!pending.length) return;
      var line = window.innerHeight * 0.92;
      pending.slice().forEach(function(el){
        if (el.getBoundingClientRect().top < line) reveal(el);
      });
    }
    function queueSweep(){
      if (sweepQueued) return;
      sweepQueued = true;
      /* A timer, not rAF: rAF stalls in occluded/background renderers — the
         same frames-never-rendered condition this sweep exists to cover. */
      window.setTimeout(sweep, 50);
    }
    window.addEventListener('scroll', queueSweep, { passive: true });
    window.addEventListener('resize', queueSweep);
    window.addEventListener('pageshow', queueSweep);
    document.addEventListener('visibilitychange', queueSweep);
    /* Whatever is on screen (or above it) shortly after load opens no matter what. */
    setTimeout(sweep, 1500);
    if (reduceMQ.addEventListener){
      reduceMQ.addEventListener('change', function(){
        if (reduced()) revs.forEach(function(el){ el.classList.add('in'); });
      });
    }
  }
  function initAll(){
    Array.prototype.forEach.call(
      document.querySelectorAll('.shopify-section--corporate-service-gallery'),
      armReveals
    );
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  /* Theme editor: re-arm a reloaded section. */
  document.addEventListener('shopify:section:load', function(e){
    if (e.target && e.target.classList && e.target.classList.contains('shopify-section--corporate-service-gallery')){
      delete e.target.dataset.corpGalInit;
      armReveals(e.target);
    }
  });

  /* ---------- Lightbox ---------- */
  var supportsVT = typeof document.startViewTransition === 'function';
  var lb = null;
  var els = null;
  var state = { list: [], pos: 0, lastFocus: null };
  var swiped = false;

  function pad2(n){ return n < 10 ? '0' + n : '' + n; }

  function build(){
    if (lb) return lb;
    lb = document.createElement('dialog');
    lb.className = 'corp-lb';
    lb.setAttribute('aria-label', 'Project photo viewer');
    lb.innerHTML =
      '<div class="corp-lb-top">' +
        '<button class="corp-lb-close" type="button">✕ &nbsp;Close</button>' +
      '</div>' +
      '<div class="corp-lb-stage">' +
        '<img alt="" width="1500" height="2000">' +
      '</div>' +
      '<div class="corp-lb-bar">' +
        '<div class="corp-lb-meta">' +
          '<p class="corp-lb-count"></p>' +
          '<p class="corp-lb-client"></p>' +
          '<p class="corp-lb-cap"></p>' +
          '<span class="corp-lb-tag"></span>' +
        '</div>' +
        '<div class="corp-lb-nav">' +
          '<button type="button" class="corp-lb-prev" aria-label="Previous photo">←</button>' +
          '<button type="button" class="corp-lb-next" aria-label="Next photo">→</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(lb);
    els = {
      img: lb.querySelector('.corp-lb-stage img'),
      stage: lb.querySelector('.corp-lb-stage'),
      count: lb.querySelector('.corp-lb-count'),
      client: lb.querySelector('.corp-lb-client'),
      cap: lb.querySelector('.corp-lb-cap'),
      tag: lb.querySelector('.corp-lb-tag'),
      close: lb.querySelector('.corp-lb-close'),
      prev: lb.querySelector('.corp-lb-prev'),
      next: lb.querySelector('.corp-lb-next')
    };

    els.close.addEventListener('click', close);
    els.prev.addEventListener('click', function(){ step(-1); });
    els.next.addEventListener('click', function(){ step(1); });
    lb.addEventListener('click', function(e){
      if (swiped){ swiped = false; return; }
      if (e.target === lb || e.target === els.stage) close();
    });
    /* Native Escape (modal dialog) — route through our close() for the morph. */
    lb.addEventListener('cancel', function(e){ e.preventDefault(); close(); });
    /* Safety net if the dialog closes by any other means. */
    lb.addEventListener('close', function(){
      lb.classList.remove('open');
      document.documentElement.classList.remove('corp-no-scroll');
    });

    /* Touch: horizontal swipe anywhere on the stage steps through the set. */
    var tx = 0, ty = 0;
    lb.addEventListener('touchstart', function(e){
      if (e.touches.length !== 1) return;
      tx = e.touches[0].clientX; ty = e.touches[0].clientY;
    }, { passive: true });
    lb.addEventListener('touchend', function(e){
      var t = e.changedTouches && e.changedTouches[0];
      if (!t) return;
      var dx = t.clientX - tx, dy = t.clientY - ty;
      if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5){
        swiped = true;
        step(dx < 0 ? 1 : -1);
      }
    }, { passive: true });

    document.addEventListener('keydown', function(e){
      if (!lb.open) return;
      if (e.key === 'Escape'){ e.preventDefault(); close(); }
      else if (e.key === 'ArrowLeft'){ e.preventDefault(); step(-1); }
      else if (e.key === 'ArrowRight'){ e.preventDefault(); step(1); }
      else if (e.key === 'Tab'){
        var f = [els.close, els.prev, els.next];
        var i = f.indexOf(document.activeElement);
        if (e.shiftKey){
          if (i <= 0){ e.preventDefault(); f[f.length - 1].focus(); }
        } else {
          if (i === f.length - 1 || i === -1){ e.preventDefault(); f[0].focus(); }
        }
      }
    });
    return lb;
  }

  function setContent(){
    var p = state.list[state.pos];
    if (!p) return;
    els.img.src = p.full; els.img.alt = p.alt;
    els.img.width = p.w; els.img.height = p.h;
    els.count.textContent = pad2(state.pos + 1) + ' — ' + pad2(state.list.length);
    els.client.textContent = p.client;
    els.cap.textContent = p.cap;
    els.tag.textContent = p.tag;
    els.tag.hidden = !p.tag;
  }

  function openModal(){
    if (lb.open) return;
    if (typeof lb.showModal === 'function') lb.showModal();
    else lb.setAttribute('open', '');
  }

  function open(list, pos, originEl){
    build();
    state.list = list; state.pos = Math.max(0, pos);
    state.lastFocus = originEl || document.activeElement;
    var srcImg = originEl ? originEl.querySelector('img') : null;
    var show = function(immediate){
      setContent();
      openModal();
      document.documentElement.classList.add('corp-no-scroll');
      if (immediate){ lb.classList.add('open'); }
      else { requestAnimationFrame(function(){ lb.classList.add('open'); }); }
      els.close.focus();
    };
    if (supportsVT && !reduced() && srcImg){
      srcImg.style.viewTransitionName = 'corp-lbhero';
      var t = document.startViewTransition(function(){
        srcImg.style.viewTransitionName = '';
        show(true);
      });
      t.finished.catch(function(){}).finally(function(){ srcImg.style.viewTransitionName = ''; });
    } else {
      show();
    }
  }

  function shut(){
    lb.classList.remove('open');
    if (typeof lb.close === 'function') lb.close();
    else lb.removeAttribute('open');
    document.documentElement.classList.remove('corp-no-scroll');
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') state.lastFocus.focus();
  }

  function close(){
    if (!lb || !lb.open) return;
    var p = state.list[state.pos];
    /* Only morph back to the origin tile if it is still on screen. */
    var tile = p ? p.el : null;
    if (tile){
      var r = tile.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) tile = null;
    }
    var thumb = tile ? tile.querySelector('img') : null;
    if (supportsVT && !reduced() && thumb){
      var t = document.startViewTransition(function(){
        thumb.style.viewTransitionName = 'corp-lbhero';
        shut();
      });
      t.finished.catch(function(){}).finally(function(){ thumb.style.viewTransitionName = ''; });
    } else {
      shut();
    }
  }

  function step(dir){
    if (!state.list.length) return;
    state.pos = (state.pos + dir + state.list.length) % state.list.length;
    setContent();
    if (!reduced() && els.img.animate){
      els.img.animate([{ opacity: 0.25 }, { opacity: 1 }], { duration: 260, easing: 'ease-out' });
    }
  }

  window.__corpLB = { open: open };

  /* Delegated wiring: the sequence is the clicked photo's own gallery section. */
  document.addEventListener('click', function(e){
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var next = e.target && e.target.closest ? e.target.closest('[data-corp-next]') : null;
    if (!next) return;
    /* The theme scrolls in-page anchors itself and records no history entry, so
       Back would leave the page. Record one: Back then returns to this project. */
    var href = next.getAttribute('href') || '';
    if (href.charAt(0) === '#' && window.location.hash !== href){
      try { window.history.pushState(null, '', href); } catch (_error) {}
    }
    var service = next.getAttribute('data-corp-service');
    var select = service && document.querySelector('[data-corp-proposal] [data-corp-dimension="service_type"]');
    if (!select || select.value) return;
    select.value = service;
    /* An unknown option leaves the select blank — the visitor simply chooses. */
    if (select.value) select.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('click', function(e){
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target && e.target.closest ? e.target.closest('[data-corp-photo]') : null;
    if (!a) return;
    var root = a.closest('[data-corp-gallery]') || document;
    var anchors = Array.prototype.slice.call(root.querySelectorAll('[data-corp-photo]'));
    var seq = anchors.map(function(x){
      return {
        el: x,
        full: x.getAttribute('href'),
        client: x.getAttribute('data-client') || '',
        tag: x.getAttribute('data-tag') || '',
        cap: x.getAttribute('data-cap') || '',
        alt: x.getAttribute('data-alt') || '',
        w: parseInt(x.getAttribute('data-w'), 10) || 1500,
        h: parseInt(x.getAttribute('data-h'), 10) || 2000
      };
    });
    e.preventDefault();
    open(seq, anchors.indexOf(a), a);
  });
})();
